import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations } from "./translations";

const LanguageContext = createContext(null);
const STORAGE_KEY = "omkar_lang";

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        if (typeof window === "undefined") return "en";
        return localStorage.getItem(STORAGE_KEY) === "kn" ? "kn" : "en";
    });

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, lang); } catch (err) { console.warn("Failed to persist language preference", err); }
        if (typeof document !== "undefined") document.documentElement.lang = lang;
    }, [lang]);

    const setLang = useCallback((next) => setLangState(next === "kn" ? "kn" : "en"), []);
    const toggle = useCallback(() => setLangState((v) => (v === "en" ? "kn" : "en")), []);

    const t = useCallback((path, fallback = "") => {
        const parts = path.split(".");
        let node = translations[lang];
        for (const p of parts) {
            if (node && Object.prototype.hasOwnProperty.call(node, p)) node = node[p];
            else { node = undefined; break; }
        }
        if (node === undefined) {
            // fallback to english if key missing in current lang
            let en = translations.en;
            for (const p of parts) {
                if (en && Object.prototype.hasOwnProperty.call(en, p)) en = en[p];
                else { en = undefined; break; }
            }
            return en ?? fallback ?? path;
        }
        return node;
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, setLang, toggle, t]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error("useLang must be used within LanguageProvider");
    return ctx;
}
